package com.wintermar.inspection.feature.data.repository.answer

import com.apollographql.apollo.ApolloClient
import com.apollographql.apollo.api.Upload
import com.wintermar.inspection.AppDatabase
import com.wintermar.inspection.core.domain.FlowResult
import com.wintermar.inspection.core.domain.Result
import com.wintermar.inspection.core.domain.handleAsFlow
import com.wintermar.inspection.core.ui.component.image.util.FileChunkModel
import com.wintermar.inspection.feature.data.local.db.SqliteCompat
import com.wintermar.inspection.feature.data.remote.mapper.answer.toFileChunkModel
import com.wintermar.inspection.feature.data.remote.request.HeaderRequest
import com.wintermar.inspection.feature.domain.repository.answer.AnswerChunkFileRepository
import com.wintermar.inspection.graphql.UploadFileMutation
import com.wintermar.inspection.graphql.type.FileCategory
import com.wintermar.inspection.graphql.type.FileUploadCommandInput
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.emptyFlow
import kotlinx.coroutines.withContext
import okio.BufferedSink

class AnswerChunkFileRepositoryImpl(
    private val apolloClient: ApolloClient,
    private val appDatabase: AppDatabase
) : AnswerChunkFileRepository {
    override suspend fun insertAnswerChunkFiles(listAnswerChunkModel: List<FileChunkModel>): Result<Unit> {
        return withContext(Dispatchers.Default) {
            try {
                appDatabase.transaction {
                    val executableQuery = SqliteCompat.selectInsertImplementation(
                        appDatabase = appDatabase,
                        upsert = appDatabase.answerChunkFileTableQueries::insertChunk,
                        replace = appDatabase.answerChunkFileTableQueries::insertReplaceChunk
                    )

                    listAnswerChunkModel.forEach { answerChunkModel ->
                        executableQuery(
                            answerChunkModel.id,
                            answerChunkModel.fileId,
                            answerChunkModel.fileName,
                            answerChunkModel.originalFileName,
                            answerChunkModel.chunkNumber.toLong(),
                            answerChunkModel.blob,
                            answerChunkModel.fileSize.toLong(),
                            answerChunkModel.chunkSize.toLong(),
                            answerChunkModel.totalFiles.toLong()
                        )
                    }

                }
                Result.Success(Unit)
            } catch (e: Exception) {
                Result.Error(e)
            }
        }
    }

    override suspend fun deleteAnswerChunkFileByAnswerFileId(answerFileId: String): Result<Unit> {
        return withContext(Dispatchers.Default) {
            try {
                appDatabase.transaction {
                    appDatabase.answerChunkFileTableQueries.deleteChunksByAnswerFileId(answerFileId)
                }
                Result.Success(Unit)
            } catch (e: Exception) {
                Result.Error(e)
            }
        }
    }

    override suspend fun getAnswerChunkFileByAnswerFileId(answerFileId: String): Result<List<FileChunkModel>> {
        return withContext(Dispatchers.Default) {
            try {
                val result = appDatabase.transactionWithResult {
                    appDatabase.answerChunkFileTableQueries
                        .getChunksByAnswerFileId(answerFileId)
                        .executeAsList()
                        .map { it.toFileChunkModel() }
                }
                Result.Success(result)
            } catch (e: Exception) {
                Result.Error(e)
            }
        }
    }

    override suspend fun deleteAnswerChunkFileById(id: String): Result<Unit> {
        return withContext(Dispatchers.Default) {
            try {
                appDatabase.transaction {
                    appDatabase.answerChunkFileTableQueries.deleteChunkById(id)
                }
                Result.Success(Unit)
            } catch (e: Exception) {
                Result.Error(e)
            }
        }
    }

    override fun uploadChunkFile(chunk: FileChunkModel): Flow<FlowResult<FileChunkModel>> {
        if (chunk.blob == null) return emptyFlow()
        return handleAsFlow(
            request = {
                apolloClient
                    .mutation(
                        UploadFileMutation(
                            command = FileUploadCommandInput(
                                id = chunk.fileId,
                                fileName = chunk.fileName,
                                chunk = object : Upload {
                                    override val contentType = "application/octet-stream"
                                    override val contentLength = chunk.blob.size.toLong()
                                    override val fileName = chunk.fileName
                                    override fun writeTo(sink: BufferedSink) {
                                        sink.write(chunk.blob)
                                    }
                                },
                                chunkIndex = chunk.chunkNumber,
                                totalChunks = chunk.totalFiles,
                                fileCategory = FileCategory.INSPECTIONS
                            )
                        )

                    )
                    .addHttpHeader(HeaderRequest.OPERATION, UploadFileMutation.OPERATION_NAME)
                    .addHttpHeader(HeaderRequest.PREFLIGHT, HeaderRequest.PREFLIGHT_VALUE)
                    .execute()
            }
        ) {
            chunk
        }
    }
}
